/* Top-level state: who is signed in, and which tab is showing.

   Sign-in is Cardea's authorization-code + PKCE flow, implemented in
   `src/auth/` and read here through `useAuth()`. This file does two things with
   it: render SignIn when there is no session, and render the shell when there
   is. There is deliberately no routing here — the /callback redirect is
   recognised by `isCallbackUrl()` (a `window.location.pathname` check in
   session.ts) and handled by AuthProvider's mount effect, because there is no
   router and one redirect URI does not justify adding one. By the time anything
   renders here the URL has already been scrubbed of `code` and `state`.

   The backend accepts the resulting token: the HTTP routes validate a Cardea
   JWT against `MILO_API_RESOURCE_URL` and scope-check `milo:read`/`milo:write`
   per route, alongside the static bearer tokens they have always taken.

   To work on the signed-in screens without a Cardea client, set VITE_DEV_USER
   in web/.env (see .env.example). It is read only under `import.meta.env.DEV`,
   so it cannot reach a production build. */
import { useState } from 'react'
import { Shell, type Tab } from './app/Shell'
import { SignIn } from './app/SignIn'
import { SCREENS } from './screens'
import { resolveLinkOutcome } from './screens/Settings'
import { useTicker } from './app/ticker'
import { ShellSyncContext, useSyncOnLoad } from './app/syncOnLoad'
import { useAuth } from './auth'

/* The dev shim, unchanged: it bypasses auth entirely rather than faking a
   session inside it, so there is no code path where the auth module believes it
   holds a credential that does not exist. Nothing that calls the API works
   under it. */
const DEV_USER = import.meta.env.DEV
  ? (import.meta.env.VITE_DEV_USER as string | undefined)
  : undefined

export default function App() {
  const { status, user, error, signIn } = useAuth()
  /* LOG unless this page load is the FatSecret callback's landing, in which case the
     outcome belongs on SETTINGS beside the pill it just changed. Still no router and
     still no new TABS member: the callback redirects to `/` with a query string, because
     Caddy's SPA allowlist is exactly `/ /index.html /callback` and any other path would
     fall through to the API and 404.

     Read in a lazy initialiser, i.e. during render, so it cannot race Settings' own read
     of the same query or the scrub that follows it.

     `resolveLinkOutcome` rather than a bare parse, because this read can happen on a
     page load that never reaches Settings: if the refresh token has lapsed, the branch
     below renders SignIn and the document leaves for Cardea, taking the URL and all of
     this state with it. The outcome is stashed in sessionStorage on the way past and
     picked up on the load that comes back. Neither reader consumes it — Settings clears
     it once the sentence is on screen. */
  const [tab, setTab] = useState<Tab>(() =>
    resolveLinkOutcome(window.location.search) ? 'SETTINGS' : 'LOG',
  )

  const signedInAs = DEV_USER ?? user

  /* The app-shell FatSecret sync (issue #88): fire the existing on-demand sync
     once per mount, watch it, and hand the screens a refresh epoch plus at most
     one staleness pill through ShellSyncContext. Here and not per-screen, so
     switching tabs cannot re-fire it — the backend's 90s debounce makes the
     repeats that do happen (reloads, second tabs) one cheap request each. */
  const shellSync = useSyncOnLoad(Boolean(signedInAs))

  /* Above the early return, because hooks are. The flag is what stops a
     signed-out page load firing three reads that can only 401 — under it the bar
     shows the mottos alone, which is what it showed before this had any data in
     it at all. */
  const ticker = useTicker(Boolean(signedInAs), shellSync.epoch)

  if (!signedInAs) {
    return (
      <SignIn
        // 'loading' covers the refresh round-trip a reload costs (the access
        // token is deliberately not persisted); 'signing-in' covers both the
        // redirect being prepared and the callback being exchanged.
        busy={status === 'loading' || status === 'signing-in'}
        error={error}
        onSignIn={signIn}
      />
    )
  }

  const Screen = SCREENS[tab]

  return (
    <Shell
      user={signedInAs}
      tab={tab}
      onTabChange={setTab}
      ticker={ticker}
      onUserClick={() => setTab('SETTINGS')}
    >
      <ShellSyncContext.Provider value={shellSync}>
        <Screen />
      </ShellSyncContext.Provider>
    </Shell>
  )
}
